import type { StreamingBlock } from "@storytell/streaming";
import { getBlockContent } from "@storytell/streaming";
import { PmNodeType } from "@storytell/types/richText";
import { TbFileCode, TbFileTextAi, TbPhotoAi } from "solid-icons/tb";
import { createMemo, onMount, Show } from "solid-js";
import { type ChatActions, useChatRuntime } from "../runtime/ChatRuntime";
import { GradientBorderStatic } from "../ui";

interface ArtifactViewerRendererProps {
  block: StreamingBlock;
}

/**
 * Which host surface opens this artifact. A host with a dedicated artifact pane
 * (bluesky's extended pane) uses it; a host without one falls back to the
 * generated-block surface it does have — the same seam the chart, presentation
 * and image cards use, which on chief opens the chat page in the browser.
 * Neither present means there is nothing to open, and the card must not offer.
 */
export const artifactOpenTarget = (
  actions: Pick<ChatActions, "openArtifact" | "openContentBlock">
): "artifactPane" | "contentBlock" | "none" => {
  if (actions.openArtifact) return "artifactPane";
  if (actions.openContentBlock) return "contentBlock";
  return "none";
};

/**
 * Renders artifact blocks - files, code, images, or other generated content
 * that users can view in detail in the extended pane.
 */
export const ArtifactViewerRenderer = (props: ArtifactViewerRendererProps) => {
  const rt = useChatRuntime();
  const content = () => {
    // Try finalContent first (for completed blocks)
    if (props.block.finalContent?.type === "text" && props.block.finalContent.text) {
      return props.block.finalContent.text;
    }
    // Fallback to streaming content from granular store
    return getBlockContent(props.block.blockId);
  };

  const artifactType = createMemo(() => {
    const text = content().toLowerCase();
    const title = props.block.title?.toLowerCase() || "";
    const activity = props.block.activity?.toLowerCase() || "";

    const allContent = `${text} ${title} ${activity}`;

    if (allContent.includes("code") || allContent.includes("script") || allContent.includes("function")) {
      return "code";
    }
    if (allContent.includes("image") || allContent.includes("picture") || allContent.includes("photo")) {
      return "image";
    }
    if (allContent.includes("document") || allContent.includes("file") || allContent.includes("pdf")) {
      return "document";
    }
    return "file";
  });

  const getArtifactIcon = () => {
    switch (artifactType()) {
      case "code":
        return <TbFileCode size={24} class="text-blue-600 dark:text-blue-400" />;
      case "image":
        return <TbPhotoAi size={24} class="text-blue-600 dark:text-blue-400" />;
      case "document":
        return <TbFileTextAi size={24} class="text-blue-600 dark:text-blue-400" />;
      default:
        return <TbFileTextAi size={24} class="text-blue-600 dark:text-blue-400" />;
    }
  };

  const title = createMemo(() => {
    const fallback = props.block.title;
    const finalContent = props.block.finalContent;
    if (finalContent?.type === "richText") {
      const heading = finalContent.richText.content
        .find((node) => node.type === PmNodeType.Heading)
        ?.content.map((node) => (node.type === PmNodeType.Text ? node.text : null))
        .filter(Boolean)
        .join("");
      return heading || fallback;
    }

    return fallback;
  });

  // Only the artifact pane follows a still-streaming artifact, so only it opens
  // unprompted. A host whose viewer is a browser jump waits to be clicked.
  onMount(() => {
    if (!props.block.isComplete) {
      rt.actions.openArtifact?.({ operationId: props.block.operationId, blockId: props.block.blockId, title: title() });
    }
  });

  const canOpen = () => artifactOpenTarget(rt.actions) !== "none";

  const handleClick = () => {
    if (artifactOpenTarget(rt.actions) === "artifactPane") {
      rt.actions.openArtifact?.({
        operationId: props.block.operationId,
        blockId: props.block.blockId,
        title: title(),
      });
      return;
    }
    rt.actions.openContentBlock?.(props.block.blockId);
  };

  return (
    <div class="relative">
      <GradientBorderStatic class="rounded-[0.6rem]" noGlow inset={-2} />
      <button
        type="button"
        data-renderer="artifact-viewer-renderer"
        disabled={!canOpen()}
        class="relative w-full rounded-lg border border-muted-border bg-panel p-4 text-left transition-all duration-200 enabled:cursor-pointer hover:enabled:bg-panel-hover hover:enabled:shadow-lg"
        onClick={handleClick}
      >
        <div class="flex items-center gap-4">
          <div class="flex shrink-0 items-center justify-center">{getArtifactIcon()}</div>
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
              <Show
                when={title()}
                fallback={<span class="text-base font-medium text-primary-text">Generated Artifact</span>}
              >
                <span class="text-base font-medium text-primary-text">{title()}</span>
              </Show>

              <Show when={!props.block.isComplete}>
                <div class="size-2 animate-pulse rounded-full bg-brand" />
              </Show>
            </div>

            <Show when={canOpen()}>
              <span class="text-xs text-secondary-text">Click to view in detail</span>
            </Show>
          </div>
        </div>
      </button>
    </div>
  );
};
